import {
  Box,
  Divider,
  Grid,
  makeStyles,
  useMediaQuery,
  useTheme,
} from '@material-ui/core'
import { Edit, Visibility } from '@material-ui/icons'
import classNames from 'classnames'
import React, { ReactElement, useMemo, useState } from 'react'
import {
  DateField,
  EditButton,
  FunctionField,
  GET_LIST,
  Record,
  ReduxState,
  Show,
  ShowProps,
  Tab,
  TabbedShowLayout,
  TabbedShowLayoutTabs,
  TextField,
  TextInput,
  TitleProps,
  useQuery,
  useShowContext,
  useTranslate,
} from 'react-admin'
import { useSelector } from 'react-redux'
import { Authority } from '../../core/auth/Authority'
import { EventDto } from '../../core/dto/event/event.dto'
import { ResourceName } from '../../core/ResourceName'
import RelationTab from '../common/RelationTab'
import relationTabFilter from '../common/RelationTabFilter'
import { useHasAuthority } from '../hooks/useHasAuthority'
import { DeactivateAccessUsingsButton } from './event-show/DeactivateAccessUsingsButton'
import { ToggleActivationButton } from './event-show/ToggleActivationButton'
import { EventStatus } from '../../core/enum/EventStatus'
import { TechnicalAssistanceProtocolButton } from './event-show/TechnicalAssistanceProtocolButton'
import { ButtonWithRecurringJob } from '../common/ButtonWithRecurringJob'
import { RecurringMethodButton } from '../common/RecurringMethodButton'
import FilteredReferenceInput from '../common/FilteredReferenceInput'
import { DefinitionOwnerTypes } from '../../core/enum/DefinitionOwnerTypes'
import { DynamicPropertyView } from '../dynamic-property/DynamicPropertyView'
import Button from '../common/customized-mui-components/Button'
// import { DynamicProperties } from '../bookings/BookingShow'

const useStyles = makeStyles((theme: any) => ({
  show: {
    width: `calc(100vw - ${theme?.sidebar?.closedWidth}px - 34px)`,
  },
  showSideOpen: {
    maxWidth: `calc(100vw - ${theme?.sidebar?.width}px - 34px)`,
  },
  showXSmall: {
    width: '100vw',
  },
  dynamicProperties: {
    '& > div': {
      display: 'flex',
      flexDirection: 'column',
    },
  },
}))

const DynamicProperties = ({ ...props }) => {
  const { record } = props
  const [editMode, setEditMode] = useState<boolean>(false)
  const classes = useStyles()

  const { refetch } = useShowContext()

  const { data: dynamicProperties } = useQuery({
    type: GET_LIST,
    resource: ResourceName.DYNAMIC_PROPERTY_DEFINITIONS,
    payload: {
      filter: {
        definitionOwnerType: DefinitionOwnerTypes.EVENT,
      },
      sort: {
        field: 'id',
        order: 'ASC',
      },
      pagination: {
        page: 1,
        perPage: 1000,
      },
    },
  })

  const handleEditMode = () => {
    setEditMode(!editMode)
  }

  return (
    <div className={classes.dynamicProperties}>
      <DynamicPropertyView
        dynamicProperties={dynamicProperties}
        editMode={editMode}
        handleEditMode={handleEditMode}
        record={record}
        refetch={refetch as () => void}
        resource={ResourceName.EVENTS}
      />
      <>
        <Divider />
        <Box p={1} display="flex" justifyContent="flex-end">
          <Button
            variant="outlined"
            onClick={handleEditMode}
            label={!editMode ? 'ra.action.edit' : 'ra.action.show'}
            startIcon={!editMode ? <Edit /> : <Visibility />}
            useSmallVersionBreakpoint={false}
          />
        </Box>
      </>
    </div>
  )
}

interface EventTitleProps extends TitleProps {
  record?: EventDto
}

const ShowTitle = (data: EventTitleProps) => {
  const { record: event } = data
  const translate = useTranslate()
  const date = useMemo(
    () =>
      !((event?.dateStart as any) instanceof Date)
        ? event?.dateStart.replace('T', ' ')
        : '',
    [event?.dateStart],
  )
  return (
    <span>
      {translate('resources.events.titles.show')}: {event?.name} {date}{' '}
      {event?.id}
    </span>
  )
}

const GeneralActions = () => {
  const { record: event } = useShowContext<EventDto>()
  const hasAuthority = useHasAuthority()

  return (
    <>
      <Divider />
      <Box p={1} display="flex" justifyContent="flex-end">
        <Grid container direction="row" justify="flex-end" alignItems="center">
          <Grid item style={{ margin: '2px' }}>
            <TechnicalAssistanceProtocolButton />
          </Grid>
          <Grid item style={{ margin: '2px' }}>
            <ButtonWithRecurringJob
              mainAction={<DeactivateAccessUsingsButton />}
              recurringAction={<DeactivateAccessUsingsButton asRecurringJob />}
            />
          </Grid>
          <Grid item>
            <EditButton
              variant="contained"
              basePath="/events"
              record={event}
              style={{ margin: '2px' }}
              disabled={!hasAuthority(Authority.EDIT_EVENTS)}
            />
          </Grid>
        </Grid>
      </Box>
    </>
  )
}

const StatusActions = () => (
  <>
    <Divider />
    <Box p={1} display="flex" justifyContent="flex-end">
      <Grid container direction="row" justify="flex-end" alignItems="center">
        <Grid item style={{ margin: '2px' }}>
          <ButtonWithRecurringJob
            mainAction={<ToggleActivationButton />}
            recurringAction={
              <RecurringMethodButton
                actions={[
                  <ToggleActivationButton
                    asRecurringJob
                    recurringStatus={EventStatus.INACTIVE}
                  />,
                  <ToggleActivationButton
                    asRecurringJob
                    recurringStatus={EventStatus.ACTIVE}
                  />,
                ]}
              />
            }
          />
        </Grid>
      </Grid>
    </Box>
  </>
)

const EventTabs = ({ ...props }) => {
  const translate = useTranslate()
  const { record: event } = props
  const hasAuthority = useHasAuthority()
  const theme = useTheme()
  const smallScreen = useMediaQuery(theme.breakpoints.down('sm'))

  const relationFilters = (...filterProps) =>
    relationTabFilter({
      ...filterProps,
      children: [<TextInput source="name" alwaysOn />],
      resource: ResourceName.EVENTS,
    })

  const devicesFilters = (...filterProps) =>
    relationTabFilter({
      ...filterProps,
      children: [
        <TextInput source="name" alwaysOn />,
        hasAuthority(Authority.VIEW_DEVICE_CATEGORIES) ? (
          <FilteredReferenceInput
            label="resources.events.filters.deviceCategoryId"
            source="categoryIdWithDescendants"
            reference={ResourceName.DEVICE_CATEGORIES}
            sort={{ field: 'name', order: 'ASC' }}
            perPage={smallScreen ? 5 : 15}
            filterSource="search"
            selectWithPaginationInputProps={{
              optionText: 'hierarchyString',
              showFilter: true,
            }}
            alwaysOn
          />
        ) : (
          <></>
        ),
      ],
      resource: ResourceName.EVENTS,
    })

  return (
    <TabbedShowLayout
      {...props}
      tabs={
        <TabbedShowLayoutTabs
          variant="scrollable"
          scrollButtons="auto"
          {...props}
        />
      }
    >
      <Tab label={translate('resources.events.tabs.general')}>
        <TextField source="id" label="ID" />
        <TextField source="name" />
        <DateField source="dateStart" showTime />
        <DateField source="dateEnd" showTime />
        {event?.externalId && <TextField source="externalId" />}
        {event?.infix && <TextField source="infix" />}
        <GeneralActions />
      </Tab>
      {hasAuthority(Authority.VIEW_DYNAMIC_PROPERTY_DEFINITIONS) && (
        <Tab
          path="dynamic-properties"
          label="resources.bookings.tabs.dynamic-properties"
        >
          <DynamicProperties />
        </Tab>
      )}
      <Tab label={translate('resources.events.tabs.status')}>
        <FunctionField
          source="status"
          render={(r: Record | undefined) => (
            <TextField
              emptyText={translate(
                `resources.enums.eventStatus.${r?.status.toLowerCase()}`,
              )}
            />
          )}
        />
        <StatusActions />
      </Tab>
      {hasAuthority(Authority.VIEW_COMPANIES) &&
        hasAuthority(Authority.EVENTS_MANAGE_COMPANIES) && (
          <Tab
            path="companies"
            label={translate('resources.events.tabs.companies')}
          >
            <>
              <RelationTab<EventDto>
                resource={ResourceName.COMPANIES}
                source="companiesIds"
                mode={
                  hasAuthority(Authority.EVENTS_MANAGE_COMPANIES)
                    ? 'edit'
                    : 'show'
                }
                attachMethod="attachCompanies"
                detachMethod="detachCompanies"
                attachRequestPayload={(r, ids) => ({
                  eventId: r.id,
                  companiesIds: ids,
                })}
                detachRequestPayload={(r, ids) => ({
                  eventId: r.id,
                  companiesIds: ids,
                })}
                refetchListAfterChange={(filters) =>
                  filters?.filterEventId === true
                }
                relationsRequestMethod="getCompanies"
                relationsRequestPayload={(r) => ({ eventId: r.id })}
                filters={relationFilters() as ReactElement[]}
                filterDefaultValues={{
                  eventId: event?.id,
                  filterEventId: false,
                }}
                sort={{
                  field: 'id',
                  order: 'DESC',
                }}
              >
                <TextField source="id" label="ID" />
                <TextField source="name" key="name" />
              </RelationTab>
            </>
          </Tab>
        )}
      {hasAuthority(Authority.VIEW_OBJECTS) && (
        <Tab path="objects" label={translate('resources.events.tabs.objects')}>
          <>
            <RelationTab<EventDto>
              resource={ResourceName.OBJECTS}
              source="objectsIds"
              mode={hasAuthority(Authority.EDIT_EVENTS) ? 'edit' : 'show'}
              attachMethod="attachObjects"
              detachMethod="detachObjects"
              attachRequestPayload={(r, ids) => ({
                eventId: r.id,
                objectsIds: ids,
              })}
              detachRequestPayload={(r, ids) => ({
                eventId: r.id,
                objectsIds: ids,
              })}
              refetchListAfterChange={(filters) =>
                filters?.filterEventId === true
              }
              filters={relationFilters() as ReactElement[]}
              filterDefaultValues={{
                eventId: event?.id,
                filterEventId: false,
              }}
              sort={{
                field: 'id',
                order: 'DESC',
              }}
            >
              <TextField source="id" label="ID" />
              <TextField source="name" key="name" />
            </RelationTab>
          </>
        </Tab>
      )}
      {hasAuthority(Authority.VIEW_ENGINES) && (
        <Tab path="engines" label={translate('resources.events.tabs.engines')}>
          <>
            <RelationTab<EventDto>
              resource={ResourceName.ENGINES}
              source="verifierDevicesIds"
              mode={hasAuthority(Authority.EDIT_EVENTS) ? 'edit' : 'show'}
              attachMethod="attachDevices"
              detachMethod="detachDevices"
              attachRequestPayload={(r, ids) => ({
                eventId: r.id,
                verifierDevicesIds: ids,
              })}
              detachRequestPayload={(r, ids) => ({
                eventId: r.id,
                verifierDevicesIds: ids,
              })}
              refetchListAfterChange={(filters) =>
                filters?.filterEventId === true
              }
              relationsRequestMethod="getDevices"
              relationsRequestPayload={(r) => ({ eventId: r.id })}
              filters={devicesFilters() as ReactElement[]}
              filterDefaultValues={{
                eventId: event?.id,
                filterEventId: false,
              }}
              sort={{
                field: 'id',
                order: 'DESC',
              }}
            >
              <TextField source="id" label="ID" />
              <TextField source="name" key="name" />
            </RelationTab>
          </>
        </Tab>
      )}
      {hasAuthority(Authority.VIEW_PALMS) && (
        <Tab path="palms" label={translate('resources.events.tabs.palms')}>
          <>
            <RelationTab<EventDto>
              resource={ResourceName.PALMS}
              source="verifierDevicesIds"
              mode={hasAuthority(Authority.EDIT_EVENTS) ? 'edit' : 'show'}
              attachMethod="attachDevices"
              detachMethod="detachDevices"
              attachRequestPayload={(r, ids) => ({
                eventId: r.id,
                verifierDevicesIds: ids,
              })}
              detachRequestPayload={(r, ids) => ({
                eventId: r.id,
                verifierDevicesIds: ids,
              })}
              refetchListAfterChange={(filters) =>
                filters?.filterEventId === true
              }
              relationsRequestMethod="getDevices"
              relationsRequestPayload={(r) => ({ eventId: r.id })}
              filters={devicesFilters() as ReactElement[]}
              filterDefaultValues={{
                eventId: event?.id,
                filterEventId: false,
              }}
              sort={{
                field: 'id',
                order: 'DESC',
              }}
            >
              <TextField source="id" label="ID" />
              <TextField source="name" key="name" />
            </RelationTab>
          </>
        </Tab>
      )}
      {hasAuthority(Authority.VIEW_DEPOSITORS) && (
        <Tab
          path="depositors"
          label={translate('resources.events.tabs.depositors')}
        >
          <>
            <RelationTab<EventDto>
              resource={ResourceName.DEPOSITORS}
              source="verifierDevicesIds"
              mode={hasAuthority(Authority.EDIT_EVENTS) ? 'edit' : 'show'}
              attachMethod="attachDevices"
              detachMethod="detachDevices"
              attachRequestPayload={(r, ids) => ({
                eventId: r.id,
                verifierDevicesIds: ids,
              })}
              detachRequestPayload={(r, ids) => ({
                eventId: r.id,
                verifierDevicesIds: ids,
              })}
              refetchListAfterChange={(filters) =>
                filters?.filterEventId === true
              }
              relationsRequestMethod="getDevices"
              relationsRequestPayload={(r) => ({ eventId: r.id })}
              filters={devicesFilters() as ReactElement[]}
              filterDefaultValues={{
                eventId: event?.id,
                filterEventId: false,
              }}
              sort={{
                field: 'id',
                order: 'DESC',
              }}
            >
              <TextField source="id" label="ID" />
              <TextField source="name" key="name" />
            </RelationTab>
          </>
        </Tab>
      )}
      {hasAuthority(Authority.VIEW_DOORS) && (
        <Tab path="doors" label={translate('resources.events.tabs.doors')}>
          <>
            <RelationTab<EventDto>
              resource={ResourceName.DOORS}
              source="verifierDevicesIds"
              mode={hasAuthority(Authority.EDIT_EVENTS) ? 'edit' : 'show'}
              attachMethod="attachDevices"
              detachMethod="detachDevices"
              attachRequestPayload={(r, ids) => ({
                eventId: r.id,
                verifierDevicesIds: ids,
              })}
              detachRequestPayload={(r, ids) => ({
                eventId: r.id,
                verifierDevicesIds: ids,
              })}
              refetchListAfterChange={(filters) =>
                filters?.filterEventId === true
              }
              relationsRequestMethod="getDevices"
              relationsRequestPayload={(r) => ({ eventId: r.id })}
              filters={devicesFilters() as ReactElement[]}
              filterDefaultValues={{
                eventId: event?.id,
                filterEventId: false,
              }}
              sort={{
                field: 'id',
                order: 'DESC',
              }}
            >
              <TextField source="id" label="ID" />
              <TextField source="name" key="name" />
            </RelationTab>
          </>
        </Tab>
      )}
      {hasAuthority(Authority.VIEW_TERMINALS) && (
        <Tab
          path="terminals"
          label={translate('resources.events.tabs.terminals')}
        >
          <>
            <RelationTab<EventDto>
              resource={ResourceName.TERMINALS}
              source="verifierDevicesIds"
              mode={hasAuthority(Authority.EDIT_EVENTS) ? 'edit' : 'show'}
              attachMethod="attachDevices"
              detachMethod="detachDevices"
              attachRequestPayload={(r, ids) => ({
                eventId: r.id,
                verifierDevicesIds: ids,
              })}
              detachRequestPayload={(r, ids) => ({
                eventId: r.id,
                verifierDevicesIds: ids,
              })}
              refetchListAfterChange={(filters) =>
                filters?.filterEventId === true
              }
              relationsRequestMethod="getDevices"
              relationsRequestPayload={(r) => ({ eventId: r.id })}
              filters={devicesFilters() as ReactElement[]}
              filterDefaultValues={{
                eventId: event?.id,
                filterEventId: false,
              }}
              sort={{
                field: 'id',
                order: 'DESC',
              }}
            >
              <TextField source="id" label="ID" />
              <TextField source="name" key="name" />
            </RelationTab>
          </>
        </Tab>
      )}
    </TabbedShowLayout>
  )
}

export const EventShow = (props: ShowProps) => {
  const classes = useStyles()

  const sidebarOpen = useSelector(
    (state: ReduxState) => state.admin.ui.sidebarOpen,
  )

  const theme = useTheme()
  const xSmallScreen = useMediaQuery(theme.breakpoints.down('xs'))

  return (
    <Show
      {...props}
      actions={false}
      title={<ShowTitle />}
      className={
        !xSmallScreen
          ? classNames(classes.show, sidebarOpen && classes.showSideOpen)
          : classes.showXSmall
      }
    >
      <EventTabs />
    </Show>
  )
}
